import {useState} from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
// import useStore from 'base/store'
import { formatDate, formatMoney, totalNumberFromArray } from 'common/helpers'
import { Loader } from 'common/components'
import { useGetAnalyticBalance, useGetAnalyticIRR, useGetAnalyticCashflow } from '..'
import { ValueBar } from './ProjectsStats.Bar'
import { Dot, Dots, ValueBlock, Chart, Footer1, Footer2 } from './ProjectsStats.styled'
import { ColorDict } from './ProjectsStats.constant'
import { Legend } from './ProjectsStats.Legend'

const Wrapper = styled.div`
  height: 400px;
  margin-bottom: 2rem;

  display: grid;
  grid-template-columns: 8fr auto auto auto;
  gap: 12px;

  & div.select {
    height: 32px;
    width: 120px;
    border-radius: 18px;
    background-color: #272727;
    display: flex;
    justify-content: center;
  }
  & select {
    border: none;
    height: 100%;
    border-radius: 18px;
    font-size: 14px;
    font-weight: 500;
    color: #FBFBFB;
    background-color: #272727;
  }
  & select:focus {
    border: none;
    outline: none;
  }
`

const RANGE = [2000000, 5000000, 10000000]

export const ProjectsStatsNew = () => {
  // const role = useStore((state) => state.role)
  const [ kpiName, setKPIName ] = useState<'balance'|'currentFC2'>('balance')
  const {data: balanceItems, isFetching: isFetchingBalance } = useGetAnalyticBalance();
  const {data: irr, isFetching: isFetchingIRR } = useGetAnalyticIRR();
  const {data: cashflowDict, isFetching: isFetchingDict } = useGetAnalyticCashflow();
  // console.log('balance', balanceItems)
  // console.log('totalIRR', irr?.totalIRR)
  // console.log('dict', cashflowDict)

  const totalBalance = totalNumberFromArray(balanceItems || [], 'balance')
  const totalFacilityLimit = totalNumberFromArray(balanceItems || [], 'currentFC2')

  const maxCashflow = !cashflowDict ? 0 : Object.values(cashflowDict).reduce((accu:number, x:any) => {
    const dd = x.drawdown || 0;
    const rp = x.repayment || 0;
    if (
      ( accu > dd) &&
      ( accu > rp)
    ) return accu;
    else if (dd > rp) return dd;
    else return rp;
  }, 0)
  const rangeCount = balanceItems?.reduce((accu:number[], x:any) => {
    if (!!x[kpiName]) {
      if      (x[kpiName] < RANGE[0]) accu[0] = accu[0] + 1;
      else if (x[kpiName] < RANGE[1]) accu[1] = accu[1] + 1;
      else if (x[kpiName] < RANGE[2]) accu[2] = accu[2] + 1;
      else                            accu[3] = accu[3] + 1;
    }
    return accu;
  }, [0, 0, 0, 0]) || [0, 0, 0, 0]
  const totalRangeCount = rangeCount.reduce((accu:number, x:number) => (accu + x), 0);
  const rangeCountPercentage = rangeCount.map((x:number) => ( x / totalRangeCount ))
  const maxRangeCountPercent = rangeCountPercentage.reduce((accu:number, x:number) => ((accu >= x) ? accu:x), 0);

  const onKPIChange = (e:any) => {
    setKPIName(e?.target?.value)
  }

  // const mockData = [
  //   [0, 1234, 12345678, 12],
  //   [12345678, 12, 0, 1234],
  // ]
  return (
    (
      isFetchingBalance ||
      isFetchingDict ||
      isFetchingIRR
    ) ? <Loader />
    :
    (
      (!balanceItems) ||
      (!irr) ||
      (!cashflowDict)
    ) ? null
    :
    <Wrapper>
      <Chart title="Loans Book Composition"
        legend={
          <div className='select'>
          <select onChange={onKPIChange}>
            <option value="balance">Loan Balance</option>
            <option value="currentFC2">Current FC2</option>
          </select>
          </div>
        }
        chart={
          <div className='row-bars' style={{padding: '42px 0 16px'}}>
          { rangeCountPercentage.map((x:number, index:number) => (
            <ValueBar key={index} maxValue={maxRangeCountPercent} values={[x]} />
          )) }
          </div>
        }
        footer={
          <Footer1>
            <Dots />
            <div className='value'>$2m</div>
            <Dots />
            <div className='value'>$5m</div>
            <Dots />
            <div className='value'>$10m</div>
            <Dots />
          </Footer1>
        }
      />
      <ValueBlock
        title="Total IRR Forecast"
        value={`${irr?.totalIRR || 0}%`}
      />
      <ValueBlock
        title="Total Balance"
        value={formatMoney(totalBalance)}
        details={formatMoney(totalFacilityLimit)}
        comments='Total Current FC2'
      />
      <Chart title="Cashflow Forecast"
        legend={<Legend />}
        chart={
          <div className='row-bars' style={{paddingTop: '28px'}}>
          { [0,1,2,3].map((index:number) => (
            <ValueBar key={index} maxValue={maxCashflow} values={[
              cashflowDict[dayjs().add(index, 'month').format('YYYY-MM')]?.drawdown,
              cashflowDict[dayjs().add(index, 'month').format('YYYY-MM')]?.repayment
            ]} />
          )) }
          </div>
        }
        footer={
          <div>
            <Footer2 className='first' style={{justifyItems: 'center'}}>
              <PlaceHolder />
            { [0,1,2,3].map((index:number) => (
              <span key={index}>
                {formatDate(dayjs().add(index, 'month').format('YYYY-MM-DD'), 'fourth')}
              </span>
            ))}
              <PlaceHolder />
            </Footer2>
            <Footer2 style={{justifyItems: 'center'}}>
              <Dot className='abs' color={ColorDict.Drawdown} />
              <PlaceHolder />
              { [0,1,2,3].map((index:number) => (
                <span key={index}>
                {
                  formatMoney(cashflowDict[dayjs().add(index, 'month').format('YYYY-MM')]?.drawdown || 0)
                  .replace('$', '')
                  // mockData[0][index]
                }
                </span>
              ))}
              <PlaceHolder />
            </Footer2>
            <Footer2 style={{justifyItems: 'center'}}>
              <Dot className='abs' color={ColorDict.Repayment} />
              <PlaceHolder />
              { [0,1,2,3].map((index:number) => (
                <span key={index}>
                {
                  formatMoney(cashflowDict[dayjs().add(index, 'month').format('YYYY-MM')]?.repayment || 0)
                  .replace('$', '')
                  // mockData[1][index]
                }
                </span>
              ))}
              <PlaceHolder />
            </Footer2>
          </div>
        }
      />
    </Wrapper>
  )
}

const PlaceHolder = () => (
  <div></div>
)
